import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../db/banco'
import { dataISOLocal } from '../pedidos/precos'
import { useAgora } from '../lib/useAgora'
import { avisosAgora, chaveDoAviso, textoDoFaltam } from '../compromissos/aviso'
import { nomeDoCliente } from '../compromissos/regras'

// ▶ Aviso com antecedencia (pedido dela 08/10, D1 a): janela que abre com o app
// aberto quando chega a hora do aviso escolhido no compromisso. Ambar, para nao
// ser confundida com a vermelha de atrasado (AlertaAtrasados). Na hora do
// compromisso ela some sozinha e, se ficar pendente, a vermelha assume.
//
// "Ja avisei" e por aparelho: guardado no navegador (com try/catch -- em aba
// anonima pode falhar, e ai vale so a memoria desta sessao).
const CHAVE = 'crm-avisos-dados'

function lerDados(): string[] {
  try {
    const bruto = localStorage.getItem(CHAVE)
    return bruto ? (JSON.parse(bruto) as string[]) : []
  } catch {
    return []
  }
}

function gravarDados(lista: string[]) {
  try {
    // So os ultimos 200: a lista nunca cresce sem fim.
    localStorage.setItem(CHAVE, JSON.stringify(lista.slice(-200)))
  } catch {
    // sem armazenamento: fica so na memoria
  }
}

export default function AlertaAviso() {
  const agora = useAgora(30_000)
  const navegar = useNavigate()
  const hoje = dataISOLocal(agora)
  const [dados, setDados] = useState<string[]>(lerDados)
  // Hoje e amanha bastam: o maior aviso e de 1 dia.
  const amanha = dataISOLocal(new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + 1))
  const proximos = useLiveQuery(
    () => banco.compromissos.where('data').between(hoje, amanha, true, true).toArray(),
    [hoje, amanha],
  )
  const clientes = useLiveQuery(() => banco.clientes.toArray(), [])
  const lista = avisosAgora(proximos ?? [], agora).filter((c) => !dados.includes(chaveDoAviso(c)))

  if (lista.length === 0) return null

  function marcarVistos() {
    const novos = [...dados, ...lista.map(chaveDoAviso)]
    setDados(novos)
    gravarDados(novos)
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4">
      <div role="alertdialog" aria-modal="true" aria-label="Aviso de compromisso" data-testid="alerta-aviso"
        className="w-full max-w-sm max-h-[90vh] flex flex-col bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 text-white px-4 pt-4 pb-6 flex items-center gap-3">
          <span className="shrink-0 w-14 h-14 rounded-full bg-white/25 ring-4 ring-white/60 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10 21h4" />
            </svg>
          </span>
          <div className="min-w-0">
            <p className="text-xl font-bold leading-tight">
              {lista.length === 1 ? 'Compromisso chegando' : `${lista.length} compromissos chegando`}
            </p>
            <p className="mt-1 text-sm text-white/90">Você pediu para ser avisado com antecedência.</p>
          </div>
        </div>

        <ul className="-mt-3 relative rounded-t-3xl bg-white px-3 pt-4 space-y-3 overflow-y-auto">
          {lista.map((c) => {
            const cliente = nomeDoCliente(clientes ?? [], c.cliente_id)
            return (
              <li key={c.id} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-2 shadow-sm">
                <div className="shrink-0 w-14 rounded-xl bg-amber-50 py-1.5 text-center">
                  <p className="font-bold text-amber-700 leading-tight">{c.hora}</p>
                  <p className="text-[11px] text-slate-500">{c.data.slice(8)}/{c.data.slice(5, 7)}</p>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-900 leading-tight line-clamp-2 break-words">{c.assunto}</p>
                  {cliente && <p className="text-xs text-slate-600 truncate">{cliente}</p>}
                  <p className="mt-0.5 text-xs font-semibold text-amber-700">{textoDoFaltam(c, agora)}</p>
                </div>
              </li>
            )
          })}
        </ul>

        <div className="px-3 pt-4 pb-4 space-y-2 bg-white">
          <button type="button" autoFocus onClick={marcarVistos}
            className="w-full rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 font-bold shadow-md">
            Ok, entendi
          </button>
          <button type="button"
            onClick={() => { marcarVistos(); navegar(`/compromissos?data=${lista[0].data}`) }}
            className="w-full rounded-2xl border-2 border-amber-300 bg-amber-50 py-2.5 font-semibold text-amber-800">
            Abrir compromissos ›
          </button>
        </div>
      </div>
    </div>
  )
}
